<template>
  <modal-dialog
    ref="dialog"
    :visible="dialog"
    :title="$t('label.x_guests', [partySize])"
    persistent
    scrollable
    max-width="500px"
    card-class="pa-0 pb-5 mb-5"
    content-class="dialog-height-50"
  >
    <!-- DELETE DIALOG -->
    <modal-dialog
      :visible="deleteDialogVisible"
      max-width="400px"
      persistent
      scrollable
    >
      <p class="text-body-1">
        {{ $t('message.confirm_delete_allocation_for_x_partysize', [partySize]) }}
      </p>
      <v-divider />
      <v-checkbox
        v-if="allowForbid"
        v-model="forbid"
        color="error"
        :label="$t('label.never_assign_allocation_again')"
      />
      <template #actions>
        <v-spacer />
        <v-btn
          color="error"
          @click="remove()"
        >
          {{ $t('button.delete') }}
        </v-btn>
        <v-btn
          text
          color="primary"
          @click="deleteDialogVisible = false"
        >
          {{ $t('button.close') }}
        </v-btn>
      </template>
    </modal-dialog>
    <!-- /// -->
    <!-- Data Table -->
    <validation-observer ref="observer">
      <v-data-table
        ref="scrollContent"
        v-reorder-table
        :headers="headers"
        :items="allocations"
        mobile-breakpoint="0"
        disable-pagination
        :disable-sort="true"
        hide-default-footer
        :no-data-text="$t('label.no_allocations')"
        @sorted="changeOrder"
      >
        <template #item.id>
          <v-icon
            v-if="allowReorder"
            class="reorder-handle"
            small
            style="cursor: move"
          >
            mdi-arrow-up-down
          </v-icon>
        </template>
        <template #item.allocatedTabItems="props">
          {{ `${props.item.tabItems.map(((e) => e.no)).join(' + ')}` }}
        </template>
        <template #item.totalSeats="props">
          {{ $t('label.x_seats', [props.item.seats]) }}
        </template>
        <template #item.actions="props">
          <v-btn
            class="pr-0 mr-0"
            icon
            @click="showDeleteDialog(props.item.id)"
          >
            <v-icon
              small
              class="pr-0 mr-0"
            >
              mdi-delete
            </v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </validation-observer>

    <!-- Actions -->
    <template #actions>
      <v-spacer />
      <v-btn
        color="primary"
        text
        @click="close"
      >
        {{ $t('button.done') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import DialogClosable from '@/mixins/dialog-closable';
import Sortable from 'sortablejs';
import Tab from '@/model/Tab';
import TabItem from '@/model/TabItem';
import { mapActions } from 'vuex';
import Allocation from '@/model/Allocation';
import { cloneModel, isModelEqualToModel, uniqueMinId } from '@/model/model-utils';
import { performSaveAction, confirmLeaveWithoutSaving } from '@/services/vue-utils';
import { AllocationsDialogMode } from './TabAllocationsDialog.vue';

export default Vue.extend({
  name: 'GuestsAllocationsDialog',
  components: { },
  mixins: [DialogClosable],
  props: {
    partySize: { type: Number, required: true },
    mode: { type: Number as PropType<AllocationsDialogMode>, required: true },
  },
  data() {
    return {
      deleteDialogVisible: false,
      headers: [
        {
          text: '',
          width: '32',
          value: 'id',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.tables'),
          value: 'allocatedTabItems',
          align: 'start',
          sortable: false,
        },
        {
          text: this.$i18n.t('label.seats'),
          value: 'totalSeats',
          align: 'end',
          sortable: false,
        },
        {
          text: '',
          width: '32',
          value: 'actions',
          align: 'center',
          sortable: false,
        },
      ],
      allocations: [] as Allocation[],
      oldAllocations: [] as Allocation[],
      selectedItemId: 0,
      forbid: false,
    };
  },
  computed: {
    allowReorder(): boolean {
      return this.mode !== AllocationsDialogMode.DisallowedAllocations;
    },
    allowForbid(): boolean {
      return this.mode !== AllocationsDialogMode.DisallowedAllocations;
    },
    allocationsByPartySize(): Map<number, Allocation[]> {
      switch (this.mode) {
        case AllocationsDialogMode.AllowedAllocations:
          return this.$tstore.getters.allowedAllocationsByPartySize;
        case AllocationsDialogMode.DisallowedAllocations:
          return this.$tstore.getters.disallowedAllocationsByPartySize;
        default: return new Map<number, Allocation[]>();
      }
    },
  },
  watch: {
    visible(value: boolean) {
      if (!value) return;
      this.reset();
    },
  },
  methods: {
    ...mapActions(['sendAllocations']),
    reset() {
      this.oldAllocations = this.allocationsByPartySize.get(this.partySize) ?? [];
      this.allocations = this.oldAllocations.map((o: Allocation) => cloneModel(o));
    },
    async changeOrder(event: Sortable.SortableEvent) {
      if (event.oldIndex === undefined || event.newIndex === undefined) return;
      const movedItem = this.allocations.splice(event.oldIndex, 1)[0];
      this.allocations.splice(event.newIndex, 0, movedItem);
      await this.save(this.allocations);
      this.reset();
    },
    showDeleteDialog(id: number) {
      this.selectedItemId = id;
      this.forbid = false;
      this.deleteDialogVisible = true;
    },
    async remove() {
      let allocations: Allocation[] = [];

      if (this.forbid) {
        allocations = this.allocations.map((o) => {
          if (o.id !== this.selectedItemId) return o;
          const no = cloneModel(o);
          no.isForbidden = true;
          return no;
        });
      } else {
        allocations = this.allocations.filter((o) => o.id !== this.selectedItemId);
      }

      const ok = await this.save(allocations);
      if (!ok) return;

      this.deleteDialogVisible = false;
      this.reset();
    },
    async save(allocations: Allocation[]) {
      const isForbidden = this.mode === AllocationsDialogMode.DisallowedAllocations;
      return performSaveAction(
        this.$refs.observer,
        async () => this.sendAllocations({ entities: allocations, partySize: this.partySize, isForbidden }),
      );
    },
    close() {
      this.$emit('close');
    },
  },
});
</script>
